import { stopReasonTexts } from './stopReasons'
import type { HomePage, PageContent, Section } from '../types/pages'

type Json = unknown
const isRecord = (v: Json): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Vult ontbrekende velden aan uit de standaard, in de volgorde van de standaard (zo staan de
 * blokken in het beheer in de volgorde van de site). Wat er al staat wint altijd; lijsten worden
 * nooit samengevoegd; onbekende sleutels uit de opslag blijven bewaard (achteraan).
 */
export function withDefaults(saved: Json, defaults: Json): Json {
  if (!isRecord(saved) || !isRecord(defaults)) return saved === undefined ? structuredClone(defaults) : saved
  const out: Record<string, Json> = {}
  for (const key of Object.keys(defaults)) {
    out[key] = key in saved ? withDefaults(saved[key], defaults[key]) : structuredClone(defaults[key])
  }
  for (const key of Object.keys(saved)) {
    if (!(key in out)) out[key] = saved[key]
  }
  return out
}

/**
 * Oudere opslag van de homepage: het blok `salon` is opgegaan in
 * `story`. De woorden gaan mee; stappen, consultregel en beeld komen uit de standaard.
 */
function upgradeHome(home: Json, defaults: HomePage | undefined): Json {
  if (!isRecord(home) || !isRecord(home.salon)) return home
  const { salon, ...rest } = home as Record<string, Json> & { salon: Section }
  if (isRecord(rest.story)) return rest
  const base = defaults?.story
  const story = {
    ...(base ? structuredClone(base) : { steps: [], consultLine: '', media: { image: '', alt: '' } }),
    ...(salon.eyebrow !== undefined ? { eyebrow: salon.eyebrow } : {}),
    heading: salon.heading,
    ...(salon.body !== undefined ? { body: salon.body } : {}),
    ...(salon.cta !== undefined ? { cta: salon.cta } : {}),
    ...(salon.secondaryCta !== undefined ? { secondaryCta: salon.secondaryCta } : {}),
  }
  return { ...rest, story }
}

/**
 * Hero van vóór de twee deuren: de webshopknop (`secondaryCta`) wordt de knoptekst van de
 * webshopdeur; de salonregel (`salonLink`) vervalt, want de salondeur neemt die rol over.
 * De rest van de deuren komt uit de standaard (via `withDefaults`).
 */
function withDoors(home: Json): Json {
  if (!isRecord(home) || !isRecord(home.hero)) return home
  const hero = home.hero
  if (!('secondaryCta' in hero) && !('salonLink' in hero)) return home
  const { secondaryCta, ...rest } = hero
  delete rest.salonLink
  const shopDoor = isRecord(rest.shopDoor) ? rest.shopDoor : typeof secondaryCta === 'string' && secondaryCta ? { cta: secondaryCta } : rest.shopDoor
  return { ...home, hero: shopDoor === undefined ? rest : { ...rest, shopDoor } }
}

/**
 * Stappen van het salonverhaal van vóór de beelden per stap: elke stap krijgt een leeg beeld
 * (= het vaste salonbeeld op de site). Lijsten vult `withDefaults` bewust niet aan, dus hier.
 */
function withStepMedia(home: Json): Json {
  if (!isRecord(home) || !isRecord(home.story) || !Array.isArray(home.story.steps)) return home
  const steps = home.story.steps as Json[]
  if (steps.every((s) => !isRecord(s) || isRecord(s.media))) return home
  const next = steps.map((s) => (isRecord(s) && !isRecord(s.media) ? { ...s, media: { image: '', alt: '' } } : s))
  return { ...home, story: { ...home.story, steps: next } }
}

/**
 * Paginateksten uit een oudere opslag in de vorm van nu brengen, vóórdat de site of het beheer
 * ze gebruikt. Puur; geeft hetzelfde object terug als er niets te doen is. Er gaat niets stil
 * verloren: omgezette woorden verhuizen mee, onbekende velden blijven staan.
 *
 * `defaults` = de standaardteksten (`pageDefaults` uit data/pages.ts). Zonder: alleen omzetten.
 */
export function upgradePageContent(p: PageContent, defaults?: PageContent): PageContent {
  const raw = p as unknown as Record<string, Json>
  let next: Record<string, Json> = { ...raw }

  if (raw.home !== undefined) next.home = withDoors(withStepMedia(upgradeHome(raw.home, defaults?.home)))

  const portal = raw.portal as PageContent['portal'] | undefined
  if (portal) {
    const texts = stopReasonTexts(portal.stopReasons)
    if (JSON.stringify(texts) !== JSON.stringify(portal.stopReasons)) next.portal = { ...portal, stopReasons: texts }
  }

  if (defaults) next = withDefaults(next, defaults) as Record<string, Json>

  return JSON.stringify(next) === JSON.stringify(raw) ? p : (next as unknown as PageContent)
}
